import { createContext, useContext } from 'react';

import { Environment, useEnvContext } from '@/core/lib/env/env.context';
import { useNativeContext } from '@/core/lib/native/native.context';
import { FeatureFlag, FeatureFlagKeys, FeatureFlagRangeDate, FeatureFlagRangeDateEnv, FeatureFlagResult, FeatureFlagToggleEnv } from '@/core/types/featureFlag.type';

export interface FFContextType {
  featureFlag?: FeatureFlagResult;
  isFeatureFlagLoading: boolean;
}

export const FFContext = createContext<FFContextType | null>(null);

export const isToggle = (featureFlag: FeatureFlagResult, key: FeatureFlagKeys) => featureFlag?.[key]?.web?.type === undefined || featureFlag?.[key]?.web?.type !== 'toggle';

export const featureFlagIsToggle = (featureFlag: FeatureFlagToggleEnv | FeatureFlagRangeDateEnv): featureFlag is FeatureFlagToggleEnv => featureFlag.type === 'toggle';

export const featureFlagIsRangeDate = (featureFlag: FeatureFlagToggleEnv | FeatureFlagRangeDateEnv): featureFlag is FeatureFlagRangeDateEnv => featureFlag.type === 'range-date';

export const isRangeDate = (featureFlag: FeatureFlagResult, key: FeatureFlagKeys) => featureFlag?.[key]?.web?.type !== undefined && featureFlag?.[key]?.web?.type === 'range-date';

export const isBetweenDates = (featureFlag: FeatureFlagRangeDate) => {
  const currentDate = new Date();
  const fromDate = new Date(featureFlag.from);
  const toDate = new Date(featureFlag.to);

  return currentDate.getTime() >= fromDate.getTime() && currentDate.getTime() <= toDate.getTime();
};

const checkCurrentEnv = <T,>(key: string | number | symbol, potentialObject: T): key is keyof T => potentialObject && typeof potentialObject === 'object' && key in potentialObject;

export const getFFForEnv = (featureFlag: FeatureFlagRangeDateEnv | FeatureFlagToggleEnv, env: Environment): FeatureFlagRangeDate | boolean => {
  if (!(env in featureFlag)) {
    return false;
  }

  if (checkCurrentEnv(env, featureFlag)) {
    return featureFlag[env];
  }
  return false;
};

export const useFFContext = () => useContext(FFContext) as FFContextType;

export const useFF = (flagName: FeatureFlagKeys): { isFFactivate: boolean; isFeatureFlagLoading: boolean } => {
  // useFFContext est typé non-nul mais renvoie null hors provider
  const { featureFlag, isFeatureFlagLoading } = useFFContext() ?? {};
  const { isNative } = useNativeContext();
  const { env } = useEnvContext();

  const flag = featureFlag?.[flagName]?.[isNative ? 'app' : 'web'];
  if (!flag) return { isFFactivate: false, isFeatureFlagLoading };

  const flagEnv = getFFForEnv(flag, env);
  if (typeof flagEnv === 'boolean') return { isFFactivate: flagEnv, isFeatureFlagLoading };

  return { isFFactivate: isBetweenDates(flagEnv), isFeatureFlagLoading };
};

export const useStripeFlag = (): { isFFactivate: boolean; isFeatureFlagLoading: boolean } => {
  const { isFFactivate, isFeatureFlagLoading } = useFF(FeatureFlag.STRIPE);
  const { platform } = useNativeContext();

  return { isFFactivate: platform !== 'ios' && isFFactivate, isFeatureFlagLoading };
};
